<template>
    <div>
        <div style="box-sizing: border-box; height: 300px; position: relative; width: 100%;">
            <div class="container">
                <div
                    v-for="(bar, barIndex) in bars"
                    class="bar-container"
                    :style="bar.barContainerStyle"
                >
                    <div class="bar-outline" :style="bar.barOutlineStyle">
                        <div class="bar" :style="bar.barStyle" />
                        <div v-if="barIndex > numStepsCompleted" class="bar-info dark">
                            <span class="no-break text-medium">{{ formatNumber(bar.step.quantity) }}</span>
                            <br>
                            <span style="display: inline-block; margin-bottom: 6px;">tot. sign.</span>
                            <br>
                            <span>motsv. tot.</span>
                            <br>
                            <span>ca. {{ pctForStep(barIndex) }} %</span>
                        </div>
                        <div v-else-if="barIndex === numStepsCompleted" class="bar-info">
                            <div v-if="! barIndex">
                                <span class="no-break text-big">{{ formatNumber(numAddressesSigned) }}</span>
                                <br>
                                <span class="text-medium">sign.</span>
                            </div>
                            <div v-else>
                                <span class="no-break text-big">{{ formatNumber(bar.step.quantity - numAddressesSigned) }}</span>
                                <br>
                                <span class="text-medium">kvar<br>till<br>bonus</span>
                            </div>
                        </div>
                        <div v-else class="bar-info">
                            <span class="no-break text-medium">{{ formatNumber(bar.step.quantity) }}</span>
                            <br>
                            <span style="display: inline-block; margin-bottom: 6px;">sign.</span>
                            <br>
                            <span class="text-big text-green">&#10003;</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="container">
                <div
                    v-for="delta in deltas"
                    class="delta-container"
                    :style="delta.deltaContainerStyle"
                >
                    <div
                        class="delta"
                        :class="delta.isReached ? 'is-reached' : delta.isInProgress ? 'is-in-progress' : ''"
                        :style="delta.deltaStyle"
                    >
                        <div v-if="delta.isReached" class="delta-info">
                            <span class="text-medium">Bonus&ensp;<span class="text-green">&#10003;</span></span>
                        </div>
                        <div v-else-if="delta.isInProgress" class="delta-info">
                            <span v-if="doDisplayMonetaryValues">
                                <span class="text-medium">&#916;&ensp;<span class="no-break">{{ formatNumber(delta.sum) }}</span></span>
                                <br>
                            </span>
                            <span>nästa bonus</span>
                        </div>
                        <div v-else class="delta-info dark">
                            <span v-if="doDisplayMonetaryValues">
                                <span class="text-medium">&#916;&ensp;<span class="no-break">{{ formatNumber(delta.sum) }}</span></span>
                                <br>
                            </span>
                            <span>nästa bonus</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div>
            <p class="info-text">
                <span class="no-break">
                    {{ formatNumber(numAddressesSigned) }}
                    <span v-if="doDisplayMonetaryValues">
                        &times; {{ formatNumber(currentPrice) }}
                        = <span class="font-bold">{{ formatNumber(currentTotal) }}</span>
                    </span>
                </span>
                &ensp;&bullet;&ensp;
                <span class="no-break">
                    Ca.
                    <span class="font-bold">{{ pctAddressesSigned }} %</span>
                </span>
            </p>
        </div>
        <div v-if="isEditable">
            <h2 class="mb-md mt-xl">Förhandsgranskning</h2>
            <fieldset class="fieldset">
                <legend class="legend">Säljomgång</legend>
                <label>
                    Antal adresser ({{ initialNumAddresses }} st)
                    <br>
                    <input
                        v-model="numAddresses"
                        type="range"
                        :max="maxAddresses"
                        :min="minAddresses"
                        step="1"
                        class="control range"
                        disabled="disabled"
                    >
                </label>
                <span>{{ numAddresses }} st</span>
                <br>
                <label>
                    Antal signerade avtal ({{ initialNumAddressesSigned }} st)
                    <br>
                    <input
                        v-model="numAddressesSigned"
                        type="range"
                        :max="numAddresses"
                        min="0"
                        step="1"
                        class="control range"
                        style="accent-color: #5e905e;"
                    >
                </label>
                <span>{{ numAddressesSigned }} st</span>
            </fieldset>
            <h2 class="mb-md mt-lg">Inställningar</h2>
            <fieldset
                v-for="(step, stepIndex) in steps"
                class="fieldset"
            >
                <legend class="legend">Steg {{ stepIndex + 1 }}</legend>
                <label>
                    Antal signaturer
                    <br>
                    <input
                        v-model.number="step.quantity"
                        type="range"
                        :max="maxAddressesForStep(stepIndex)"
                        :min="minAddressesForStep(stepIndex)"
                        step="1"
                        class="control range"
                    >
                </label>
                <span>{{ formatNumber(step.quantity) }} sign. (ca. {{ pctForStep(stepIndex) }} %)</span>
                <br>
                <label>
                    Styckpris
                    <br>
                    <input
                        v-model.number="step.price"
                        type="number"
                        :max="maxPriceForStep(stepIndex)"
                        :min="minPriceForStep(stepIndex)"
                        step="1"
                        class="control number"
                    >
                </label>
                <br>
                <button
                    type="button"
                    class="btn btn-sm is-danger mb-sm mt-md float-right"
                    @click="removeStep(stepIndex)"
                >
                    Ta bort steg {{ stepIndex + 1 }}
                </button>
            </fieldset>
            <fieldset class="fieldset">
                <legend class="legend">Trappa</legend>
                <button
                    type="button"
                    class="btn btn-sm is-ghost my-sm"
                    @click="addStep"
                >
                    Lägg till steg
                </button>
            </fieldset>
        </div>
        <input
            v-model="stepsJson"
            type="hidden"
            name="sales_penetration_steps"
        >
    </div>
</template>
<script>
    export default {
        props: {
            doDisplayMonetaryValues: {
                type: Boolean,
                required: false,
                default: false,
            },
            initialNumAddresses: {
                type: Number,
                required: true,
            },
            initialNumAddressesSigned: {
                type: Number,
                required: true,
            },
            initialSteps: {
                type: Array,
                required: false,
                default: () => [],
            },
            isEditable: {
                type: Boolean,
                required: false,
                default: false,
            },
        },

        data() {
            return {
                hueFrom: 18, //0,
                hueTo: 120, //240,
                numAddresses: this.initialNumAddresses,
                numAddressesSigned: this.initialNumAddressesSigned,
                steps: this.initialSteps,
            };
        },

        computed: {
            bars: function () {
                const bars = [];
                const numStepsCompleted = this.numStepsCompleted;
                const stepWidth = Math.floor(1 / this.numSteps * 100);

                for (let i = 0; i < this.numSteps; i++) {
                    const hue = this.hueFrom - i * this.hueStepDiff;
                    const isThisStepCompleted = numStepsCompleted > i;
                    const isThisStepInProgress = numStepsCompleted === i;
                    const step = this.steps[i];
                    const stepHeight = Math.round((i + 1) / this.numSteps * 100);

                    const barHeight = isThisStepInProgress
                        ? this.numAddressesSigned / step.quantity * 100
                        : 100;
                    const border = isThisStepInProgress ? '1px solid black' : isThisStepCompleted ? 'none' : '1px solid darkgray';
                    const lightness = isThisStepInProgress ? 48 : isThisStepCompleted ? 90 : 100;
                    const saturation = isThisStepInProgress ? 24 : 0;

                    const barContainerStyle = {
                        height: stepHeight + '%',
                        width: stepWidth + '%',
                    };
                    const barOutlineStyle = {
                        border,
                    };
                    const barStyle = {
                        backgroundColor: 'hsl(' + hue + 'deg ' + saturation + '% ' + lightness + '%)',
                        // borderBottom: '1px solid hsl(' + hue + 'deg 100% 24%)',
                        // borderLeft: '6px solid hsl(' + hue + 'deg 100% 24%)',
                        // borderRight: '1px solid hsl(' + hue + 'deg 100% 36%)',
                        // borderTop: '6px solid hsl(' + hue + 'deg 100% 36%)',
                        height: barHeight + '%',
                    };

                    bars.push({
                        step,
                        barContainerStyle,
                        barOutlineStyle,
                        barStyle,
                    });
                }

                return bars;
            },
            currentPrice: function () {
                if (! this.numSteps) return 0;
                if (! this.numStepsCompleted) return this.steps[0].price;

                return this.steps[Math.min(this.numStepsCompleted - 1, this.numSteps - 1)].price;
            },
            currentTotal: function () {
                return this.numAddressesSigned * this.currentPrice;
            },
            deltas: function () {
                const baseWidth = Math.floor(1 / this.numSteps * 100);
                const deltas = [];
                let deltaSum = 0;
                let nextStep;
                const numDeltas = this.numSteps - 1;
                let step;

                for (let i = 0; i < numDeltas; i++) {
                    nextStep = this.steps[i + 1];
                    step = this.steps[i];

                    const height = Math.round((i + 2) / this.numSteps * 100) - Math.round((i + 1) / this.numSteps * 100);
                    const isInProgress = this.numStepsCompleted - 1 === i;
                    const isReached = this.numDeltasReached > i;
                    const width = (i + 1) * baseWidth;

                    deltaSum = (nextStep.quantity - step.quantity) * (nextStep.price - step.price);

                    const deltaContainerStyle = {
                        height: height + '%',
                        width: width + '%',
                    };

                    deltas.unshift({
                        deltaContainerStyle,
                        isInProgress,
                        isReached,
                        sum: deltaSum,
                    });
                }

                return deltas;
            },
            hueStepDiff: function () {
                return (this.hueFrom - this.hueTo) / (this.numSteps - 1);
            },
            maxAddresses: function () {
                return Math.max(100, this.initialNumAddresses * 10);
            },
            minAddresses: function () {
                return this.numSteps + 1;
            },
            numDeltasReached: function () {
                return Math.max(0, this.numStepsCompleted - 1);
            },
            numSteps: function () {
                return this.steps.length;
            },
            numStepsCompleted: function () {
                let step;
                let numStepsCompleted = 0;

                for (var i = 0; i < this.numSteps; i++) {
                    step = this.steps[i];

                    if (step.quantity > this.numAddressesSigned) return numStepsCompleted;

                    numStepsCompleted++;
                }

                return numStepsCompleted;
            },
            pctAddressesSigned: function () {
                if (! this.numAddresses) return 0;

                return Math.round(this.numAddressesSigned / this.numAddresses * 100);
            },
            stepsJson: function () {
                return JSON.stringify(this.steps);
            },
        },

        beforeMount: function () {
            if (! this.numSteps) {
                for (let i = 0; i < 3; i++) {
                    this.addStep();
                }
            }
        },

        methods: {
            addStep: function () {
                let price = 3000;
                let quantity = Math.round(this.numAddresses * .4);

                if (this.numSteps) {
                    const prevStep = this.steps[this.numSteps - 1];

                    const perdec = Math.min(10, Math.round(prevStep.quantity / this.numAddresses * 10) + 1);

                    price = prevStep.price + 500;

                    for (let i = prevStep.quantity; i <= this.numAddresses; i++) {
                        quantity = i;

                        if (quantity / this.numAddresses * 10 >= perdec) break;
                    }
                }

                this.steps.push({
                    price,
                    quantity,
                });
            },
            formatNumber: function (price) {
                const priceStr = '' + price;
                let result = '';

                for (let i = 0; i < priceStr.length; i++) {
                    if (! ((priceStr.length - i) % 3)) {
                        result = result + ' ';
                    }

                    result = result + priceStr[i];
                }

                result = result.trim();

                return result;
            },
            maxAddressesForStep: function (stepIndex) {
                if (stepIndex >= this.numSteps - 1) return this.numAddresses;

                return this.steps[stepIndex + 1].quantity - 1;
            },
            maxPriceForStep: function (stepIndex) {
                if (stepIndex >= this.numSteps - 1) return 1000000;

                return this.steps[stepIndex + 1].price - 1;
            },
            minAddressesForStep: function (stepIndex) {
                if (! stepIndex) return 1;

                return Math.min(this.numAddresses, this.steps[stepIndex - 1].quantity + 1);
            },
            minPriceForStep: function (stepIndex) {
                if (! stepIndex) return 1;

                return this.steps[stepIndex - 1].price + 1;
            },
            pctForStep: function (stepIndex) {
                if (! this.numAddresses) return 0;

                const step = this.steps[stepIndex];

                return Math.round(step.quantity / this.numAddresses * 100);
            },
            priceForStep: function (stepIndex) {
                const step = this.steps[stepIndex];

                return step.quantity * step.price;
            },
            removeStep: function (stepIndex) {
                this.steps.splice(stepIndex, 1);
            },
        },
    };
</script>
<style scoped>
    .bar {
        bottom: 0;
        height: 100%;
        position: absolute;
        width: 100%;
    }

    .bar-container {
        display: inline-block;
        padding: 0 3px;
        position: relative;
    }

    .bar-info {
        color: white;
        font-size: 14px;
        font-weight: bold;
        height: 100%;
        line-height: 18px;
        padding: 12px 6px;
        position: absolute;
        text-align: center;
        text-shadow: 0 0 2px black, 0 0 2px black, 0 0 2px black;
        top: 0;
        width: 100%;
    }

    .bar-info.dark {
        color: #b8b8b8;
        text-shadow: none;
    }

    .bar-outline {
        height: 100%;
        overflow: hidden;
        position: relative;
        width: 100%;
    }

    .container {
        height: 100%;
        position: absolute;
        width: 100%;
    }

    .control {
        width: 50%;
    }

    .control.range {
        accent-color: #3f3c3c;
    }

    .control.number {
        border-width: 1px;
    }

    .delta {
        border: 1px dashed darkgray;
        border-radius: 6px;
        height: 100%;
        overflow: hidden;
        position: relative;
        width: 100%;
    }

    .delta.is-in-progress {
        background-color: hsl(51deg 100% 72%);
        border: 1px dashed black;
    }

    .delta.is-reached {
        background-color: hsl(51deg 100% 84%);
        border: none;
    }

    .delta-container {
        padding: 0 3px 6px 3px;
    }

    .delta-info {
        color: white;
        font-size: 14px;
        font-weight: bold;
        height: 100%;
        padding: 12px 6px;
        position: absolute;
        text-align: center;
        text-shadow: 0 0 2px black, 0 0 2px black, 0 0 2px black;
        top: 0;
        width: 100%;
    }

    .delta-info.dark {
        color: #b8b8b8;
        text-shadow: none;
    }

    .fieldset {
        border-color: #d6d6d6;
        border-style: groove;
        border-width: 2px;
        display: block;
        margin-bottom: 18px;
        margin-inline-start: 2px;
        margin-inline-end: 2px;
        min-inline-size: min-content;
        padding-block-start: 0.35em;
        padding-inline-start: 0.75em;
        padding-inline-end: 0.75em;
        padding-block-end: 0.625em;
    }

    .info-text {
        margin: 3px 0 0 0;
        padding: 0 3px;
        text-align: center;
    }

    .legend {
        border-color: initial;
        border-style: none;
        border-width: initial;
        display: block;
        font-weight: bold;
        padding-inline-start: 2px;
        padding-inline-end: 2px;
    }

    .no-break {
        white-space: nowrap;
    }

    .text-big {
        font-size: 24px;
        line-height: 30px;
    }

    .text-green {
        color: rgb(94, 144, 94);
    }

    .text-medium {
        font-size: 18px;
        line-height: 21px;
    }
</style>
